import { useMemo } from "react";
import { formatTimeAgo } from "@/components/agent/display/utils/formatters";
import {
  GenieConsoleState,
  LifecycleBandView,
} from "@/types/dashboard/genie-console";
import { GenieBrand } from "./genieBrand";
import { BuildPreview } from "./previews/BuildPreview";
import { KnowledgePreview } from "./previews/KnowledgePreview";
import { ReachPreview } from "./previews/ReachPreview";
import { VoicePreview } from "./previews/VoicePreview";

const hexToRgb = (hex: string) => {
  const value = hex.replace("#", "");
  return {
    r: parseInt(value.substring(0, 2), 16) || 0,
    g: parseInt(value.substring(2, 4), 16) || 0,
    b: parseInt(value.substring(4, 6), 16) || 0,
  };
};

const toHex = (value: number) => Math.round(value).toString(16).padStart(2, "0");

/**
 * Sample a colour from the signature gradient at `t` (0 → 1).
 *
 * Every tint on this page is a point on the genie's own gradient (or, with no
 * custom branding, the Help Genie #4E9CFF → #FF4DB8 signature). Nothing here
 * introduces a colour that isn't already on that line.
 */
const sampleGradient = (from: string, to: string, t: number): string => {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return `#${toHex(a.r + (b.r - a.r) * t)}${toHex(a.g + (b.g - a.g) * t)}${toHex(
    a.b + (b.b - a.b) * t,
  )}`;
};

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

/**
 * Turns the console state into the four lifecycle bands — BRAIN, REACH, VOICE,
 * BUILD — with a live data state line and a live preview of the user's own
 * content for each. Every layout variant (HG-1225) renders this same list, so
 * the directions differ in chrome only, never in data.
 */
export const useLifecycleBands = (
  state: GenieConsoleState,
  brand: GenieBrand,
): LifecycleBandView[] => {
  const genie = state.genie.data.selectedGenie;
  const genieId = genie?.id ?? null;

  return useMemo(() => {
    const from = brand.primary;
    const to = brand.secondary || "#FF4DB8";
    const tints = [0, 0.34, 0.67, 1].map((t) => sampleGradient(from, to, t));

    const knowledge = state.knowledge.data;
    const reach = state.reach.data;
    const voice = state.voice.data;
    const build = state.build.data;

    const knowledgeLine = knowledge.documentCount
      ? [
          `${knowledge.documentCount} ${plural(knowledge.documentCount, "document", "documents")}`,
          knowledge.lastUpdatedAt ? `updated ${formatTimeAgo(knowledge.lastUpdatedAt)}` : null,
          knowledge.staleDocuments.length
            ? `${knowledge.staleDocuments.length} ageing`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "Nothing to answer from yet";

    const liveChannels = reach.channels.filter((channel) => channel.live).length;
    const reachLine = genie
      ? [
          `${liveChannels} of ${reach.channels.length} ways in are live`,
          reach.scanCount
            ? `${reach.scanCount} ${plural(reach.scanCount, "scan", "scans")}`
            : "no scans yet",
          reach.packCount
            ? `${reach.packCount} ${plural(reach.packCount, "pack", "packs")}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "No Genie to share yet";

    const voiceLine = voice.conversationCount
      ? [
          `${voice.conversationCount} ${plural(
            voice.conversationCount,
            "conversation",
            "conversations",
          )} in 30 days`,
          voice.unreviewedCount ? `${voice.unreviewedCount} unread` : "all read",
        ].join(" · ")
      : "Nobody has talked to it yet";

    const buildLine = build.totalCount
      ? `${build.completedCount} of ${build.totalCount} set up${
          build.otherGenies.length
            ? ` · ${build.otherGenies.length} other ${plural(
                build.otherGenies.length,
                "Genie",
                "Genies",
              )}`
            : ""
        }`
      : "Nothing built yet";

    const knowledgeRoute = genieId
      ? `/genie-hub/knowledge-base/${genieId}`
      : "/genie-hub/knowledge-base";

    return [
      {
        id: "brain",
        label: "BRAIN",
        tagline: "What your Genie knows",
        genieIcon: "brain",
        tint: tints[0],
        stateLine: knowledgeLine,
        preview: (
          <KnowledgePreview band={state.knowledge} brandColor={tints[0]} genieId={genieId} />
        ),
        linkTo: knowledgeRoute,
        linkLabel: "Open knowledge",
        isLoading: state.knowledge.isLoading,
        isError: state.knowledge.isError,
      },
      {
        id: "reach",
        label: "REACH",
        tagline: "Where people find it",
        genieIcon: "share",
        tint: tints[1],
        stateLine: reachLine,
        preview: <ReachPreview band={state.reach} brandColor={tints[1]} genie={genie} />,
        linkTo: "/genie-hub/activation",
        linkLabel: "Open activation",
        isLoading: state.reach.isLoading,
        isError: state.reach.isError,
      },
      {
        id: "voice",
        label: "VOICE",
        tagline: "What customers actually said",
        genieIcon: "microphone",
        tint: tints[2],
        stateLine: voiceLine,
        preview: (
          <VoicePreview band={state.voice} brandColor={tints[2]} hasGenie={Boolean(genie)} />
        ),
        linkTo: "/genie-hub/inbox",
        linkLabel: "Open inbox",
        isLoading: state.voice.isLoading,
        isError: state.voice.isError,
      },
      {
        id: "build",
        label: "BUILD",
        tagline: "What's left to make it great",
        genieIcon: "wizard-hat",
        tint: tints[3],
        stateLine: buildLine,
        preview: <BuildPreview band={state.build} brandColor={tints[3]} genieId={genieId} />,
        linkTo: genieId ? `/genie-hub/my-genies/${genieId}` : "/genie-hub/my-genies",
        linkLabel: "Open Genie",
        isLoading: state.build.isLoading,
        isError: state.build.isError,
      },
    ];
  }, [state, brand.primary, brand.secondary, genie, genieId]);
};
